import type { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities/index';
import type { CSSObject } from '@mantine/core';
import {
	Card,
	createStyles,
	Group,
	LoadingOverlay,
	Menu,
	Stack,
} from '@mantine/core';
import { useHover } from '@mantine/hooks';
import { showNotification } from '@mantine/notifications';
import { useApiRemoveBiDashboardMetric } from '@repo/api-codegen';
import { OvalLoader } from '@repo/common/components/Loader/index.ts';
import { UserRole } from '@repo/common/enums/UserRole.ts';
import { Icon, IconButton, Text } from '@repo/foundations';
import { buttonHoverTransition } from '@repo/theme/primitives/transitions.ts';
import { isNil } from 'lodash-es';
import { observer } from 'mobx-react-lite';
import { memo, Suspense, useCallback, useMemo } from 'react';
import { useCheckIAMPermission } from '../../utils/authorization/roles.ts';
import { formatRelativeTimeAgo } from '../../utils/time.ts';
import { MetricSkeleton } from '../BiDashboard/components/BiDashboardSkeleton.tsx';
import type { BIDashboardStore } from '../BiDashboard/store';
import { BiMetricChartRender } from './components/BiMetricChartRender.tsx';
import { useBiMetricExport } from './hooks/useBiMetricExport';
import { useSuspenseBiMetricStore } from './hooks/useSuspenseBiMetricStore.ts';

interface BIMetricControllerProps {
	metricId: string;
	mode?: 'view_only' | 'full_edit';
	dashboardStore?: BIDashboardStore;
	onHeaderClick: () => void;
	activatorRef?: (element: HTMLElement | null) => void;
	listeners?: SyntheticListenerMap;
	isDragging?: boolean;
}

interface BIMetricControllerStylesProps {
	hovered: boolean;
	isDraggable: boolean;
	isDragging: boolean;
}

const useStyles = createStyles(
	(
		theme,
		{ hovered, isDraggable, isDragging }: BIMetricControllerStylesProps
	) => {
		const dragCursor = isDragging ? 'grabbing' : 'grab';
		const headerCursor = isDraggable ? dragCursor : 'default';

		return {
			card: {
				padding: '0 !important',
				backgroundColor: theme.other.getColor('surface/primary/default'),
				borderRadius: theme.other.borderRadius.md,
				boxShadow: theme.shadows.sm,
			},
			viewOnlyHeader: {
				cursor: headerCursor,
				padding: `${theme.spacing.sm} ${theme.spacing.sm} ${theme.spacing.sm} ${theme.spacing.md}`,
				justifyContent: 'space-between',
				flexWrap: 'nowrap',
			},
			viewOnlyHeaderContent: {
				flexGrow: '1 !important' as CSSObject['flexGrow'],
				gap: 0,
			},
			viewOnlyHeaderButtons: {
				flexWrap: 'nowrap',
				gap: 0,
				opacity: hovered ? 1 : 0,
				pointerEvents: hovered ? 'auto' : 'none',
				...buttonHoverTransition('opacity'),
			},
		};
	}
);

const useBiMetricStyles = createStyles((theme) => ({
	fontSize: theme.other.typography.text.sm,
}));

const BIMetricControllerInternal = observer(
	({
		metricId,
		mode = 'view_only',
		dashboardStore,
		onHeaderClick,
		activatorRef,
		listeners,
		isDragging,
	}: BIMetricControllerProps) => {
		const { ref, hovered } = useHover();

		const { store } = useSuspenseBiMetricStore(metricId, false, dashboardStore);

		// Check permission to manage dashboard (for dragging metrics)
		const { hasPermission: hasManagePermission } = useCheckIAMPermission({
			v1AllowedRoles: [UserRole.ADMIN, UserRole.EDITOR],
			v2Permission: 'Dashboards.Manage',
		});

		const isDraggable = useMemo(
			() =>
				hasManagePermission &&
				'view_only' === mode &&
				!isNil(activatorRef) &&
				!isNil(listeners),
			[hasManagePermission, mode, activatorRef, listeners]
		);

		const { classes } = useStyles({
			hovered,
			isDraggable,
			isDragging: !!isDragging,
		});

		const { mutateAsync: removeBiDashboardMetric } =
			useApiRemoveBiDashboardMetric({});

		const handleNavigateToMetric = () => {
			onHeaderClick();
		};

		const handleRefreshMetric = useCallback(() => {
			store.refresh();
		}, [store]);

		const handleRemoveFromDashboard = useCallback(async () => {
			if (!dashboardStore) return;

			await removeBiDashboardMetric({
				pathParams: {
					biDashboardId: dashboardStore.id,
					biMetricId: metricId,
				},
			});

			// Show success notification
			showNotification({
				title: 'Metric removed',
				message: 'The metric has been removed from this dashboard',
				color: 'green',
			});
		}, [dashboardStore, metricId, removeBiDashboardMetric]);

		const {
			handleCopyAsPng: handleCopyAsPNG,
			handleDownloadPng: handleDownloadPNG,
		} = useBiMetricExport({
			biMetricId: store.metricVersion.bi_metric_id,
			metricName: store.metricVersion.name,
		});

		const timeFrame = useMemo(() => {
			if (!store.metricVersion.time_frame) return '';

			return `${store.metricVersion.time_frame} •`;
		}, [store.metricVersion]);

		return (
			<Card ref={ref} className={classes.card}>
				<LoadingOverlay
					loader={<OvalLoader size="lg" />}
					visible={store.isRefreshing}
				/>
				{'view_only' === mode && (
					<Group className={classes.viewOnlyHeader}>
						<Stack
							ref={isDraggable ? activatorRef : undefined}
							className={classes.viewOnlyHeaderContent}
							{...(isDraggable ? listeners : {})}
						>
							<Text weight="semibold" lineClamp={1}>
								{store.metricVersion.name}
							</Text>
							<Text size="xs" color="text/secondary/default" lineClamp={1}>
								{timeFrame ? `${timeFrame} ` : ''}
								{store.metricVersion.description}
							</Text>
						</Stack>
						<Group className={classes.viewOnlyHeaderButtons}>
							<IconButton
								iconName="chevronRight"
								variant="tertiary"
								tooltip="Go to metric"
								onClick={handleNavigateToMetric}
							/>
							<Menu>
								<Menu.Target>
									<IconButton
										iconName="dots"
										variant="tertiary"
										tooltip="More actions"
									/>
								</Menu.Target>
								<Menu.Dropdown>
									{hasManagePermission && (
										<Menu.Item
											onClick={handleRefreshMetric}
											icon={<Icon name="refresh" />}
										>
											<Stack spacing={0}>
												<Text size="sm">Refresh data</Text>
												{store.latestServerMetricVersion.last_run_at && (
													<Text size="xs" color="text/secondary/default">
														Data from{' '}
														{formatRelativeTimeAgo(
															store.latestServerMetricVersion.last_run_at
														)}
													</Text>
												)}
											</Stack>
										</Menu.Item>
									)}
									<Menu.Item
										onClick={handleCopyAsPNG}
										icon={<Icon name="copy" />}
									>
										<Text size="sm">Copy as PNG</Text>
									</Menu.Item>
									<Menu.Item
										onClick={handleDownloadPNG}
										icon={<Icon name="download" />}
									>
										<Text size="sm">Download PNG</Text>
									</Menu.Item>
									{dashboardStore && hasManagePermission && (
										<Menu.Item
											onClick={handleRemoveFromDashboard}
											icon={<Icon name="x" />}
										>
											<Text size="sm">Remove...</Text>
										</Menu.Item>
									)}
								</Menu.Dropdown>
							</Menu>
						</Group>
					</Group>
				)}
				<BiMetricChartRender
					metricVersion={store.metricVersion}
					mode={mode}
					shadow={mode === 'full_edit'}
				/>
			</Card>
		);
	}
);

export const BiMetricController = memo((props: BIMetricControllerProps) => (
	<Suspense fallback={<MetricSkeleton key="metric-skeleton" />}>
		<BIMetricControllerInternal {...props} />
	</Suspense>
));
